import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCoachConversations,
  createCoachConversation,
  getCoachMessages,
  deleteCoachConversation,
  streamCoachMessage,
  ApiError,
  type CoachConversation,
  type CoachMessage,
} from '../api.js';
import { parseMarkdownLite, type Block, type InlineSpan } from '../lib/markdownLite.js';
import { Icon, IconButton, Page, Sheet } from '../ui/index.js';

const SUGGESTIONS = [
  'How did I sleep this week?',
  'Is my resting heart rate trending up?',
  'What should I eat tonight?',
  'Am I recovered enough to train hard today?',
];

/** Render inline **bold** / `code` spans. */
function Inline({ spans }: { spans: InlineSpan[] }): JSX.Element {
  return (
    <>
      {spans.map((s, i) => {
        if (s.type === 'bold') return <strong key={i}>{s.text}</strong>;
        if (s.type === 'code') return <code key={i} className="coach-code">{s.text}</code>;
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}

/** Render a markdown-lite assistant reply (paragraphs, lists, code). */
function Markdown({ text }: { text: string }): JSX.Element {
  const blocks: Block[] = parseMarkdownLite(text);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'p') return <p key={i} className="coach-msg__p"><Inline spans={b.spans} /></p>;
        if (b.type === 'code')
          return <pre key={i} className="coach-msg__code"><code>{b.text}</code></pre>;
        if (b.type === 'ul')
          return (
            <ul key={i} className="coach-msg__list">
              {b.items.map((it, j) => <li key={j}><Inline spans={it} /></li>)}
            </ul>
          );
        return (
          <ol key={i} className="coach-msg__list">
            {b.items.map((it, j) => <li key={j}><Inline spans={it} /></li>)}
          </ol>
        );
      })}
    </>
  );
}

export default function Coach(): JSX.Element {
  const [conversations, setConversations] = useState<CoachConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // The composer grows with what is typed (up to ~6 lines), and shrinks back after send.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [input]);

  const loadConversations = useCallback(async (): Promise<CoachConversation[]> => {
    const list = await getCoachConversations();
    setConversations(list);
    return list;
  }, []);

  const loadMessages = useCallback(async (id: string) => {
    const { messages: msgs } = await getCoachMessages(id);
    setMessages(msgs);
  }, []);

  // Initial load: conversations, then the newest one's messages.
  useEffect(() => {
    loadConversations()
      .then((list) => {
        if (list.length > 0) {
          setActiveId(list[0]!.id);
          return loadMessages(list[0]!.id);
        }
        return undefined;
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load conversations.'),
      );
  }, [loadConversations, loadMessages]);

  // Keep the thread scrolled to the newest content.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamText]);

  async function selectConversation(id: string): Promise<void> {
    if (streaming) return;
    setListOpen(false);
    setActiveId(id);
    setError(null);
    try {
      await loadMessages(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages.');
    }
  }

  async function newConversation(): Promise<void> {
    if (streaming) return;
    setListOpen(false);
    setError(null);
    try {
      const conv = await createCoachConversation();
      setConversations((prev) => [conv, ...prev]);
      setActiveId(conv.id);
      setMessages([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start a conversation.');
    }
  }

  async function removeConversation(id: string): Promise<void> {
    if (streaming) return;
    try {
      await deleteCoachConversation(id);
      const remaining = conversations.filter((c) => c.id !== id);
      setConversations(remaining);
      if (activeId === id) {
        const next = remaining[0]?.id ?? null;
        setActiveId(next);
        if (next) await loadMessages(next);
        else setMessages([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation.');
    }
  }

  async function send(preset?: string): Promise<void> {
    const text = (preset ?? input).trim();
    if (!text || streaming || disabled) return;

    setError(null);
    let conversationId = activeId;
    // Lazily create a conversation if none is active.
    if (!conversationId) {
      try {
        const conv = await createCoachConversation();
        setConversations((prev) => [conv, ...prev]);
        conversationId = conv.id;
        setActiveId(conv.id);
        setMessages([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start a conversation.');
        return;
      }
    }

    // Optimistically show the user's message.
    const optimistic: CoachMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: text,
      meta: null,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput('');
    setStreamText('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamCoachMessage(
        conversationId,
        text,
        {
          onDelta: (delta) => setStreamText((prev) => prev + delta),
          onDone: () => {},
          onError: (message) => setError(message),
        },
        controller.signal,
      );
      // Reload canonical messages (server text is authoritative) + refresh titles.
      await loadMessages(conversationId);
      await loadConversations();
    } catch (err) {
      if (err instanceof ApiError && err.status === 501) {
        setDisabled(true);
        // The turn never ran server-side; drop the optimistic user bubble.
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setInput(text);
      } else if (controller.signal.aborted) {
        // User aborted: keep what streamed, reload to persist canonical state.
        try {
          await loadMessages(conversationId);
        } catch {
          // ignore
        }
      } else {
        setError(err instanceof Error ? err.message : 'The coach failed to respond.');
      }
    } finally {
      setStreaming(false);
      setStreamText('');
      abortRef.current = null;
    }
  }

  function abort(): void {
    abortRef.current?.abort();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  const active = conversations.find((c) => c.id === activeId);
  const list = (
    <ul className="bc-convos" aria-label="Conversations">
      {conversations.length === 0 ? (
        <li className="bc-convos__empty">No conversations yet.</li>
      ) : (
        conversations.map((c) => (
          <li key={c.id} className={`bc-convo${c.id === activeId ? ' bc-convo--on' : ''}`}>
            <button
              type="button"
              className="bc-convo__title"
              onClick={() => void selectConversation(c.id)}
              disabled={streaming}
              title={c.title ?? 'New conversation'}
              aria-current={c.id === activeId ? 'true' : undefined}
            >
              {c.title ?? 'New conversation'}
            </button>
            <button
              type="button"
              className="bc-convo__del"
              onClick={() => void removeConversation(c.id)}
              disabled={streaming}
              aria-label="Delete conversation"
            >
              <Icon name="trash" size={16} />
            </button>
          </li>
        ))
      )}
    </ul>
  );

  return (
    <Page
      title="Coach"
      className="bc-coachpage"
      wide
      actions={
        <>
          <span className="bc-coach__phone">
            <IconButton icon="history" label="Chats" onClick={() => setListOpen(true)} />
            <IconButton icon="pencil" label="New chat" onClick={() => void newConversation()} />
          </span>
        </>
      }
    >
      <div className="bc-coach">
        <aside className="bc-coach__side" aria-label="Chats">
          <button type="button" className="bc-coach__new" onClick={() => void newConversation()} disabled={streaming}>
            <Icon name="pencil" size={18} />
            New chat
          </button>
          {list}
        </aside>

        <section className="bc-coach__main" aria-label={active?.title ?? 'Conversation'}>
          {error && (
            <p className="bc-banner bc-coach__error" role="alert">
              <Icon name="alert" size={18} />
              {error}
            </p>
          )}
          <div className="bc-coach__thread" ref={threadRef}>
            {messages.length === 0 && !streaming ? (
              <div className="bc-coach__welcome">
                <span className="bc-coach__mark" aria-hidden="true">
                  <Icon name="chat" size={26} />
                </span>
                <p className="bc-coach__hello">Ask about your health</p>
                <p className="bc-coach__hint">Sleep, heart rate, workouts, food, labs: it reads your real numbers.</p>
                {!disabled && (
                  <div className="bc-coach__chips">
                    {SUGGESTIONS.map((q) => (
                      <button key={q} type="button" className="bc-coach__chip" onClick={() => void send(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`coach-msg bc-msg bc-msg--${m.role}`}>
                  {m.role === 'assistant' ? <Markdown text={m.content} /> : <p className="coach-msg__p">{m.content}</p>}
                </div>
              ))
            )}
            {streaming && (
              <div className="coach-msg bc-msg bc-msg--assistant bc-msg--live">
                {streamText ? (
                  <Markdown text={streamText} />
                ) : (
                  <span className="bc-typing" aria-label="Thinking">
                    <span />
                    <span />
                    <span />
                  </span>
                )}
              </div>
            )}
          </div>

          {disabled && (
            <p className="bc-banner bc-coach__disabled" role="alert">
              The AI coach isn't set up on this server (no API key), so chat is off.
            </p>
          )}

          <div className="bc-composer">
            <textarea
              ref={inputRef}
              className="bc-composer__input"
              placeholder="Ask your coach…"
              aria-label="Message"
              rows={1}
              value={input}
              disabled={streaming || disabled}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
            />
            {streaming ? (
              <button type="button" className="bc-composer__btn bc-composer__btn--stop" onClick={abort} aria-label="Stop">
                <Icon name="stop" size={16} />
              </button>
            ) : (
              <button
                type="button"
                className="bc-composer__btn"
                onClick={() => void send()}
                disabled={disabled || input.trim().length === 0}
                aria-label="Send"
              >
                <Icon name="arrow-up" size={20} />
              </button>
            )}
          </div>
        </section>
      </div>

      {listOpen && (
        <Sheet title="Chats" onClose={() => setListOpen(false)} action={{ label: 'New', onClick: () => void newConversation() }}>
          {list}
        </Sheet>
      )}
    </Page>
  );
}
